import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useRealtime } from "@/lib/useRealtime";
import { api } from "@/lib/apiClient";
import {
  employeeLoginFromRecord,
  type HeroEmployeeRecord,
} from "@/lib/employees";
import {
  getBoard,
  getHistoryBoard,
  updateAssignment,
  deleteAssignment,
  createAssignment,
  deleteUnavailability,
  unlinkAssignment,
  updateAssignmentSeriesMode,
  requestAssignmentFeinplanung,
  fetchFeinplanungRequestsOpenCount,
  fetchPendingFeinplanungRequests,
  approveFeinplanungRequest,
  rejectFeinplanungRequest,
  fetchMonteurProposalsOpenCount,
  approveMonteurProposal,
  rejectMonteurProposal,
  parseMontageplanHighlight,
  scheduleAblage,
  parkAssignmentInAblage,
  DEFAULT_PLANUNG_COLOR,
} from "@/lib/montageplanung";
import type {
  PlanungAblageItem,
  PlanungAbsence,
  PlanungAssignment,
  PlanungBoard,
  PlanungCalendarDay,
  PlanungFeinplanungRequest,
  PlanungFeinplanungRequestSummary,
  PlanungMonteurProposalListItem,
  PlanungRow,
  PlanungUnavailability,
} from "@/lib/montageplanung";
import {
  addDays,
  diffDays,
  enumerateDays,
  groupConsecutive,
  isoWeek,
  isNonWorkDay,
  isSameDay,
  isWeekend,
  MONTH_NAMES,
  nonWorkDayTitle,
  parseIso,
  startOfDay,
  toIso,
} from "./dateUtils";
import { PlanningHeadRow } from "./PlanningHeadRow";
import { PlanningLegend } from "./PlanningLegend";
import { PlanningSettingsDialog } from "./PlanningSettingsDialog";
import { PlanningToolbar } from "./PlanningToolbar";
import { AssignmentDialog } from "./AssignmentDialog";
import { AssignmentViewDialog } from "./AssignmentViewDialog";
import { FinePlanningDialog } from "./FinePlanningDialog";
import { FinePlanningRequestsDialog } from "./FinePlanningRequestsDialog";
import { MonteurProposalDialog } from "./MonteurProposalDialog";
import { MonteurProposalsDialog } from "./MonteurProposalsDialog";
import { AssignmentLinksDialog } from "./AssignmentLinksDialog";
import { RowManagerDialog } from "./RowManagerDialog";
import { HistoryDialog } from "./HistoryDialog";
import { AssignmentBar } from "./AssignmentBar";
import { BookedAssignmentBar } from "./BookedAssignmentBar";
import type { BookedColorMode } from "./bookedAssignments";
import { AbsenceBar } from "./AbsenceBar";
import { AbsenceApprovalDialog } from "./AbsenceApprovalDialog";
import { UnavailabilityDialog } from "./UnavailabilityDialog";
import { CompanyEventDialog } from "./CompanyEventDialog";
import { AblagePanel } from "./AblagePanel";
import { ablageDragHint, placeAblage, type AblagePlacement } from "./ablagePlacement";
import {
  defaultUnavailableDay,
  findUnavailabilityOnDay,
  isUnavailableDay,
  unavailabilityDayTitle,
} from "./unavailabilityUtils";
import { UnavailabilityReasonLabels } from "./UnavailabilityReasonLabels";
import type { AssignmentDragMode } from "./AssignmentBar";
import {
  useAssignmentDrag,
  type AssignmentDragPreview,
  type AssignmentDragState,
} from "./useAssignmentDrag";
import "./montageplanung.css";
import { ContextMenu, type ContextMenuState } from "@/components/ui/ContextMenu";
import {
  assignmentToClipboard,
  clipboardToAssignmentInput,
  readPasteLinkedPreference,
  writePasteLinkedPreference,
  type PlanungAssignmentClipboard,
} from "./assignmentClipboard";
import { findAbsenceConflicts, formatAbsenceConflictMessage } from "./absenceConflicts";
import { notifyMontageplanungBadgeRefresh } from "@/lib/navBadges";
import { useConfirmDialog } from "./useConfirmDialog";
import { barTopForLane, computeRowTrackLayout } from "./assignmentLayout";
import { useBookedAssignments } from "./useBookedAssignments";
import { absenceLayoutKey } from "./vacationSicknessOverlay";
import { rowLabelAbbrev, rowLabelFull } from "./displayLabels";
import { MerosStateBars } from "./MerosPlanRow";
import { mergePlanungBoard, replacePlanungBoardRange } from "./planningBoardMerge";
import { usePlanningFullscreen } from "./usePlanningFullscreen";
import { useScrollPan } from "./useScrollPan";
import {
  formatMontageplanDayHours,
  formatMontageplanOvertimeBalance,
  getDailyHoursForCell,
  getDailyPaidHoursForCell,
  getOvertimeBalanceForEmployee,
  useMontageplanTimesOverlay,
} from "./useMontageplanDailyHours";
import {
  MP_DAYCELL_BUFFER_DAYS,
  MP_INITIAL_HALF_DAYS,
  MP_LAYOUT_DESKTOP,
  MP_LAYOUT_MOBILE,
  MP_LOAD_CHUNK_DAYS,
  MP_LOAD_THRESHOLD_DAYS,
  MP_SEPARATOR_ROW_H,
  type MpLayoutMetrics,
} from "./layoutConstants";

interface DialogState {
  open: boolean;
  row: PlanungRow | null;
  assignment: PlanungAssignment | null;
  defaultDate: string;
}

interface ViewDialogState {
  open: boolean;
  assignment: PlanungAssignment | null;
  row: PlanungRow | null;
}

interface UnavailabilityDialogState {
  open: boolean;
  row: PlanungRow | null;
  existing: PlanungUnavailability | null;
  defaultFrom: string;
  defaultTo: string;
}

interface CompanyEventDialogState {
  open: boolean;
  defaultDate: string;
  preselectedRowId: number | null;
  assignment: PlanungAssignment | null;
}

interface FinePlanningState {
  open: boolean;
  assignment: PlanungAssignment | null;
}

interface AssignmentLinksState {
  open: boolean;
  assignment: PlanungAssignment | null;
}

interface LinkPickState {
  primaryId: number;
  selectedIds: number[];
  excludedIds: number[];
  /** Dialog geschlossen – Auswahl per Klick auf Balken im Plan. */
  boardPickActive: boolean;
}

export function MontageplanungIndexPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompact = useMediaQuery("(max-width: 767px)");
  const metrics: MpLayoutMetrics = isCompact ? MP_LAYOUT_MOBILE : MP_LAYOUT_DESKTOP;
  const { labelW: LABEL_W, dayW: DAY_W, barH: BAR_H } = metrics;

  const today = useMemo(() => startOfDay(new Date()), []);
  const initialFrom = useMemo(() => addDays(today, -MP_INITIAL_HALF_DAYS), [today]);
  const initialTo = useMemo(() => addDays(today, MP_INITIAL_HALF_DAYS), [today]);

  const [rangeFrom, setRangeFrom] = useState(initialFrom);
  const [rangeTo, setRangeTo] = useState(initialTo);
  const [board, setBoard] = useState<PlanungBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [extending, setExtending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({
    open: false,
    row: null,
    assignment: null,
    defaultDate: "",
  });
  const [viewDialog, setViewDialog] = useState<ViewDialogState>({
    open: false,
    assignment: null,
    row: null,
  });
  const [rowManagerOpen, setRowManagerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyScope, setHistoryScope] = useState<{
    title: string;
    entityIds: number[];
  } | null>(null);
  const [historyLogId, setHistoryLogId] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [finePlanning, setFinePlanning] = useState<FinePlanningState>({
    open: false,
    assignment: null,
  });
  const [assignmentLinks, setAssignmentLinks] = useState<AssignmentLinksState>({
    open: false,
    assignment: null,
  });
  const [linkPickState, setLinkPickState] = useState<LinkPickState | null>(null);
  const [clipboard, setClipboard] = useState<PlanungAssignmentClipboard | null>(null);
  const [pasteLinked, setPasteLinked] = useState(readPasteLinkedPreference);
  const [activeDrag, setActiveDrag] = useState<AssignmentDragState | null>(null);
  const [activeDragMode, setActiveDragMode] = useState<AssignmentDragMode | null>(null);
  const [dragPreview, setDragPreview] = useState<AssignmentDragPreview | null>(null);
  const [linkHighlightIds, setLinkHighlightIds] = useState<ReadonlySet<number>>(new Set());
  const [planFocusHighlightIds, setPlanFocusHighlightIds] = useState<ReadonlySet<number>>(
    new Set(),
  );
  const [feinplanungRequestsCount, setFeinplanungRequestsCount] = useState(0);
  const [monteurProposalsCount, setMonteurProposalsCount] = useState(0);
  const [pendingFeinplanungRequests, setPendingFeinplanungRequests] = useState<
    PlanungFeinplanungRequest[]
  >([]);
  const [monteurProposalDialog, setMonteurProposalDialog] = useState<DialogState>({
    open: false,
    row: null,
    assignment: null,
    defaultDate: "",
  });
  const pendingPlanFocusRef = useRef<{
    assignmentId: number;
    dateFrom: string;
    rowId: number;
  } | null>(null);
  const [absenceApproval, setAbsenceApproval] = useState<PlanungAbsence | null>(null);
  const [feinplanungRequestsOpen, setFeinplanungRequestsOpen] = useState(false);
  const [monteurProposalsOpen, setMonteurProposalsOpen] = useState(false);
  const [requestingFeinplanungId, setRequestingFeinplanungId] = useState<number | null>(null);
  const [reviewingFeinplanungRequest, setReviewingFeinplanungRequest] = useState<{
    id: number;
    action: "approve" | "reject";
  } | null>(null);
  const [reviewingMonteurProposal, setReviewingMonteurProposal] = useState<{
    id: number;
    action: "approve" | "reject";
  } | null>(null);
  const [unavailDialog, setUnavailDialog] = useState<UnavailabilityDialogState>({
    open: false,
    row: null,
    existing: null,
    defaultFrom: "",
    defaultTo: "",
  });
  const [companyEventDialog, setCompanyEventDialog] = useState<CompanyEventDialogState>({
    open: false,
    defaultDate: "",
    preselectedRowId: null,
    assignment: null,
  });
  const [ablageDrag, setAblageDrag] = useState<{
    item: PlanungAblageItem;
    placement: AblagePlacement | null;
    hint: string;
  } | null>(null);
  const [ablageRefresh, setAblageRefresh] = useState(0);
  const [ablageStatus, setAblageStatus] = useState<string | null>(null);
  const [schedulingAblage, setSchedulingAblage] = useState(false);
  const [ablageParkHover, setAblageParkHover] = useState(false);
  const ablageDropRef = useRef<HTMLElement | null>(null);
  const suppressBarClickRef = useRef(false);
  const suppressAblageClickRef = useRef(false);
  const planningShellRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollSnapshotRef = useRef(0);
  const realtimeRefreshTimerRef = useRef<number | null>(null);
  const activeDragRef = useRef<AssignmentDragState | null>(null);
  const pendingRealtimeRefreshRef = useRef(false);
  const legendRef = useRef<HTMLDivElement | null>(null);
  const headTrackScrollRef = useRef<HTMLDivElement | null>(null);
  const trackRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const extendLockRef = useRef(false);
  const pendingScrollAdjustRef = useRef(0);
  const centeredOnTodayRef = useRef(false);
  const [headScroll, setHeadScroll] = useState({ scrollLeft: 0, trackVisibleWidth: 0 });
  const { confirm, dialog: confirmDialog, confirmOpen } = useConfirmDialog();

  const immersiveModalOpen =
    dialog.open ||
    viewDialog.open ||
    finePlanning.open ||
    assignmentLinks.open ||
    linkPickState?.boardPickActive ||
    rowManagerOpen ||
    settingsOpen ||
    historyOpen ||
    absenceApproval != null ||
    feinplanungRequestsOpen ||
    monteurProposalsOpen ||
    monteurProposalDialog.open ||
    unavailDialog.open ||
    companyEventDialog.open ||
    contextMenu != null ||
    confirmOpen;

  const { fullscreen: planningFullscreen, toggle: togglePlanningFullscreenBase } =
    usePlanningFullscreen({ blockEscapeExit: immersiveModalOpen });

  const togglePlanningFullscreen = useCallback(() => {
    if (scrollRef.current) {
      scrollSnapshotRef.current = scrollRef.current.scrollLeft;
    }
    togglePlanningFullscreenBase();
  }, [togglePlanningFullscreenBase]);

  const windowFrom = rangeFrom;
  const days = useMemo(() => enumerateDays(rangeFrom, rangeTo), [rangeFrom, rangeTo]);
  const lastIdx = days.length - 1;
  const todayIdx = useMemo(
    () => days.findIndex((d) => isSameDay(d, today)),
    [days, today],
  );
  const totalWidth = days.length * DAY_W;

  const getTrackMetrics = useCallback(
    (scrollEl: HTMLDivElement) => ({
      scrollLeft: scrollEl.scrollLeft,
      trackVisibleWidth: Math.max(0, scrollEl.clientWidth - LABEL_W),
    }),
    [LABEL_W],
  );

  const fromIso = toIso(rangeFrom);
  const toIsoStr = toIso(rangeTo);

  const fetchBoardForRange = useCallback(
    (from: Date, to: Date) => {
      const fromStr = toIso(from);
      const toStr = toIso(to);
      return historyLogId
        ? getHistoryBoard(historyLogId, { date_from: fromStr, date_to: toStr })
        : getBoard({ date_from: fromStr, date_to: toStr });
    },
    [historyLogId],
  );

  const syncOpenAssignmentFromBoard = useCallback((data: PlanungBoard) => {
    setDialog((d) => {
      if (!d.open || !d.assignment) return d;
      const fresh = data.assignments.find((a) => a.id === d.assignment!.id);
      return fresh ? { ...d, assignment: fresh } : d;
    });
    setViewDialog((d) => {
      if (!d.open || !d.assignment) return d;
      const fresh = data.assignments.find((a) => a.id === d.assignment!.id);
      return fresh ? { ...d, assignment: fresh } : d;
    });
    setFinePlanning((fp) => {
      if (!fp.open || !fp.assignment) return fp;
      const fresh = data.assignments.find((a) => a.id === fp.assignment!.id);
      return fresh ? { ...fp, assignment: fresh } : fp;
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchBoardForRange(rangeFrom, rangeTo)
      .then((data) => {
        setBoard(data);
        syncOpenAssignmentFromBoard(data);
      })
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : historyLogId
              ? "Historischer Stand konnte nicht geladen werden."
              : "Montageplanung konnte nicht geladen werden.",
        ),
      )
      .finally(() => setLoading(false));
  }, [fetchBoardForRange, rangeFrom, rangeTo, historyLogId, syncOpenAssignmentFromBoard]);

  const refreshBoard = useCallback(() => {
    // Nach Scroll-Nachladen kann der akkumulierte Range sehr groß sein.
    // Live-Refresh lädt nur den sichtbaren Bereich (+ Puffer) und merged ihn ein.
    const scrollEl = scrollRef.current;
    let from = rangeFrom;
    let to = rangeTo;
    if (scrollEl && days.length > 0) {
      const { scrollLeft, trackVisibleWidth } = getTrackMetrics(scrollEl);
      const startIdx = Math.max(
        0,
        Math.floor(scrollLeft / DAY_W) - MP_INITIAL_HALF_DAYS,
      );
      const endIdx = Math.min(
        days.length - 1,
        Math.ceil((scrollLeft + trackVisibleWidth) / DAY_W) + MP_INITIAL_HALF_DAYS,
      );
      from = days[startIdx] ?? rangeFrom;
      to = days[endIdx] ?? rangeTo;
    }
    const fromIsoStr = toIso(from);
    const toIsoRefresh = toIso(to);
    fetchBoardForRange(from, to)
      .then((data) =>
        setBoard((prev) =>
          prev && (from > rangeFrom || to < rangeTo)
            ? replacePlanungBoardRange(prev, data, fromIsoStr, toIsoRefresh)
            : data,
        ),
      )
      .catch((err) =>
        setError(
          err instanceof Error ? err.message : "Planung konnte nicht live aktualisiert werden.",
        ),
      );
  }, [DAY_W, days, fetchBoardForRange, getTrackMetrics, rangeFrom, rangeTo]);

  const refreshBoardRef = useRef(refreshBoard);
  refreshBoardRef.current = refreshBoard;

  useEffect(() => {
    activeDragRef.current = activeDrag;
    if (!activeDrag && pendingRealtimeRefreshRef.current) {
      pendingRealtimeRefreshRef.current = false;
      refreshBoardRef.current();
    }
  }, [activeDrag]);

  useRealtime({
    scopes: ["montageplanung:list", "domain:montageplanung"],
    domains: ["montageplanung", "employee-times"],
    events: ["montageplanung_changed", "employee-times_changed"],
    enabled: historyLogId == null,
    onChange: () => {
      if (activeDragRef.current) {
        pendingRealtimeRefreshRef.current = true;
        return;
      }
      if (realtimeRefreshTimerRef.current != null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
      }
      realtimeRefreshTimerRef.current = window.setTimeout(() => {
        realtimeRefreshTimerRef.current = null;
        refreshBoardRef.current();
      }, 300);
    },
  });

  useEffect(() => {
    return () => {
      if (realtimeRefreshTimerRef.current != null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
      }
    };
  }, []);

  const resetToTodayView = useCallback(() => {
    centeredOnTodayRef.current = false;
    extendLockRef.current = false;
    pendingScrollAdjustRef.current = 0;
    const from = addDays(today, -MP_INITIAL_HALF_DAYS);
    const to = addDays(today, MP_INITIAL_HALF_DAYS);
    setRangeFrom(from);
    setRangeTo(to);
    setLoading(true);
    setError(null);
    fetchBoardForRange(from, to)
      .then((data) => setBoard(data))
      .catch((err) =>
        setError(
          err instanceof Error
            ? err.message
            : historyLogId
              ? "Historischer Stand konnte nicht geladen werden."
              : "Montageplanung konnte nicht geladen werden.",
        ),
      )
      .finally(() => setLoading(false));
  }, [today, fetchBoardForRange, historyLogId]);

  const highlightQuery = searchParams.get("highlight");
  const highlightFromQuery = searchParams.get("from");
  const highlightToQuery = searchParams.get("to");
  const suppressTodayResetRef = useRef(false);

  const extendLeft = useCallback(async () => {
    if (extendLockRef.current || extending) return;
    extendLockRef.current = true;
    setExtending(true);
    const chunk = MP_LOAD_CHUNK_DAYS;
    const newFrom = addDays(rangeFrom, -chunk);
    const chunkTo = addDays(rangeFrom, -1);
    try {
      const data = await fetchBoardForRange(newFrom, chunkTo);
      setBoard((prev) => mergePlanungBoard(prev, data));
      pendingScrollAdjustRef.current += chunk * DAY_W;
      setRangeFrom(newFrom);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Frühere Termine konnten nicht geladen werden.");
    } finally {
      extendLockRef.current = false;
      setExtending(false);
    }
  }, [DAY_W, extending, fetchBoardForRange, rangeFrom]);

  const extendRight = useCallback(async () => {
    if (extendLockRef.current || extending) return;
    extendLockRef.current = true;
    setExtending(true);
    const chunk = MP_LOAD_CHUNK_DAYS;
    const newTo = addDays(rangeTo, chunk);
    const chunkFrom = addDays(rangeTo, 1);
    try {
      const data = await fetchBoardForRange(chunkFrom, newTo);
      setBoard((prev) => mergePlanungBoard(prev, data));
      setRangeTo(newTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Spätere Termine konnten nicht geladen werden.");
    } finally {
      extendLockRef.current = false;
      setExtending(false);
    }
  }, [extending, fetchBoardForRange, rangeTo]);

  const checkExtendEdges = useCallback(
    (scrollEl: HTMLDivElement) => {
      if (extendLockRef.current || extending) return;
      const { scrollLeft, trackVisibleWidth } = getTrackMetrics(scrollEl);
      const thresholdPx = MP_LOAD_THRESHOLD_DAYS * DAY_W;
      if (scrollLeft <= thresholdPx) {
        void extendLeft();
        return;
      }
      if (scrollLeft + trackVisibleWidth >= totalWidth - thresholdPx) {
        void extendRight();
      }
    },
    [DAY_W, extending, extendLeft, extendRight, getTrackMetrics, totalWidth],
  );

  useLayoutEffect(() => {
    if (centeredOnTodayRef.current || loading || todayIdx < 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const dayCenter = todayIdx * DAY_W + DAY_W / 2;
    const visibleTrackWidth = Math.max(0, el.clientWidth - LABEL_W);
    el.scrollLeft = Math.max(0, dayCenter - visibleTrackWidth / 2);
    centeredOnTodayRef.current = true;
    setHeadScroll(getTrackMetrics(el));
  }, [loading, todayIdx, DAY_W, LABEL_W, days.length, getTrackMetrics]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const adjust = pendingScrollAdjustRef.current;
    if (!el || adjust === 0) return;
    el.scrollLeft += adjust;
    pendingScrollAdjustRef.current = 0;
    setHeadScroll(getTrackMetrics(el));
    checkExtendEdges(el);
  }, [rangeFrom, checkExtendEdges, getTrackMetrics]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = scrollSnapshotRef.current;
    setHeadScroll(getTrackMetrics(el));
    const inner = headTrackScrollRef.current;
    if (inner) {
      inner.style.transform = `translateX(${-el.scrollLeft}px)`;
    }
  }, [planningFullscreen, getTrackMetrics]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || extending) return;
    checkExtendEdges(el);
  }, [checkExtendEdges, extending, totalWidth]);

  const isHistorical = board?.is_historical ?? historyLogId != null;
  const canEdit = (board?.can_edit ?? false) && !isHistorical;
  const canCreateMonteurProposal =
    (board?.can_create_monteur_proposal ?? false) && !isHistorical;
  const canApproveFeinplanungRequests =
    (board?.can_approve_feinplanung_requests ?? false) && !isHistorical;
  const canApproveMonteurProposals =
    (board?.can_approve_monteur_proposals ?? false) && !isHistorical;
  const canViewWorkHours = board?.can_view_work_hours ?? false;
  const canViewAblage = (board?.can_view_ablage ?? false) && !isHistorical;

  function isMonteurPendingProposal(assignment: PlanungAssignment): boolean {
    return assignment.monteur_proposal_status === "pending";
  }

  function shouldUseMonteurProposalDialog(assignment: PlanungAssignment | null): boolean {
    if (!canCreateMonteurProposal) return false;
    if (!assignment) return true;
    return isMonteurPendingProposal(assignment) && !!assignment.can_edit_monteur_proposal;
  }

  function canEditAssignment(assignment: PlanungAssignment): boolean {
    if (isHistorical) return false;
    if (canEdit) {
      return assignment.monteur_proposal_status !== "rejected";
    }
    return isMonteurPendingProposal(assignment) && !!assignment.can_edit_monteur_proposal;
  }

  function isMerosRow(row: { system_key?: string | null } | null | undefined): boolean {
    return row?.system_key === "meros";
  }

  function canDragAssignment(assignment: PlanungAssignment): boolean {
    const row = board?.rows.find((item) => item.id === assignment.row_id);
    if (isMerosRow(row)) return false;
    return canEdit && canEditAssignment(assignment);
  }

  function monteurCanProposeOnRow(row: PlanungRow): boolean {
    if (isMerosRow(row)) return false;
    return canCreateMonteurProposal && (row.kind === "employee" || row.kind === "resource");
  }

  function rowIsEditableForViewer(row: PlanungRow): boolean {
    if (isMerosRow(row)) return false;
    if (canEdit) return true;
    return monteurCanProposeOnRow(row);
  }

  function canEditFeinplanung(assignment: PlanungAssignment): boolean {
    return canEdit || !!assignment.can_edit_feinplanung;
  }

  function canRequestFeinplanung(assignment: PlanungAssignment): boolean {
    if (assignment.is_company_event) return false;
    if (canEdit || isHistorical) return false;
    if (assignment.can_edit_feinplanung || assignment.feinplanung_request_pending) return false;
    return assignment.can_request_feinplanung !== false;
  }

  function feinplanungRequestPending(assignment: PlanungAssignment): boolean {
    return !!assignment.feinplanung_request_pending;
  }

  async function handleRequestFeinplanung(assignment: PlanungAssignment) {
    setRequestingFeinplanungId(assignment.id);
    setError(null);
    try {
      await requestAssignmentFeinplanung(assignment.id);
      setViewDialog((prev) => {
        if (!prev.assignment || prev.assignment.id !== assignment.id) return prev;
        return {
          ...prev,
          assignment: {
            ...prev.assignment,
            can_request_feinplanung: false,
            feinplanung_request_pending: true,
          },
        };
      });
      await load();
      notifyMontageplanungBadgeRefresh();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Antrag konnte nicht gestellt werden.",
      );
    } finally {
      setRequestingFeinplanungId(null);
    }
  }

  async function handleApproveOpenFeinplanungRequest(assignment: PlanungAssignment) {
    const request = resolveOpenFeinplanungRequest(assignment);
    if (!request) return;
    setReviewingFeinplanungRequest({ id: request.id, action: "approve" });
    setError(null);
    try {
      await approveFeinplanungRequest(request.id);
      await load();
      refreshPendingFeinplanungRequests();
      notifyMontageplanungBadgeRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Freigabe fehlgeschlagen.");
    } finally {
      setReviewingFeinplanungRequest(null);
    }
  }

  async function handleRejectOpenFeinplanungRequest(assignment: PlanungAssignment) {
    const request = resolveOpenFeinplanungRequest(assignment);
    if (!request) return;
    const who = request.employee_display?.trim() || request.employee_login;
    const ok = await confirm({
      title: "Feinplanungs-Antrag ablehnen",
      message: `Antrag von ${who} für „${assignment.title}" wirklich ablehnen?`,
      confirmLabel: "Ablehnen",
      variant: "danger",
    });
    if (!ok) return;
    setReviewingFeinplanungRequest({ id: request.id, action: "reject" });
    setError(null);
    try {
      await rejectFeinplanungRequest(request.id);
      await load();
      refreshPendingFeinplanungRequests();
      notifyMontageplanungBadgeRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setReviewingFeinplanungRequest(null);
    }
  }

  async function handleApproveMonteurProposal(assignment: PlanungAssignment) {
    setReviewingMonteurProposal({ id: assignment.id, action: "approve" });
    setError(null);
    try {
      await approveMonteurProposal(assignment.id);
      await load();
      refreshPendingMonteurProposals();
      notifyMontageplanungBadgeRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Annahme fehlgeschlagen.");
    } finally {
      setReviewingMonteurProposal(null);
    }
  }

  async function handleRejectMonteurProposal(assignment: PlanungAssignment) {
    const who = assignment.monteur_proposal_by_login?.trim() || "Monteur";
    const ok = await confirm({
      title: "Terminantrag ablehnen",
      message: `Terminantrag von ${who} für „${assignment.title}" wirklich ablehnen?`,
      confirmLabel: "Ablehnen",
      variant: "danger",
    });
    if (!ok) return;
    setReviewingMonteurProposal({ id: assignment.id, action: "reject" });
    setError(null);
    try {
      await rejectMonteurProposal(assignment.id);
      await load();
      refreshPendingMonteurProposals();
      notifyMontageplanungBadgeRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setReviewingMonteurProposal(null);
    }
  }

  function pendingRequestToSummary(
    request: PlanungFeinplanungRequest,
  ): PlanungFeinplanungRequestSummary {
    return {
      id: request.id,
      employee_login: request.employee_login,
      employee_display: request.employee_display,
      requested_at: request.requested_at,
    };
  }

  function resolveOpenFeinplanungRequest(
    assignment: PlanungAssignment | null,
  ): PlanungFeinplanungRequestSummary | null {
    if (!assignment || !canApproveFeinplanungRequests) return null;
    if (assignment.open_feinplanung_request) {
      return assignment.open_feinplanung_request;
    }
    const direct = pendingFeinplanungRequests.find(
      (request) => request.assignment_id === assignment.id,
    );
    if (direct) return pendingRequestToSummary(direct);

    const relatedIds = new Set<number>([
      assignment.id,
      ...(assignment.linked_assignment_ids ?? []),
    ]);
    const linked = pendingFeinplanungRequests.find((request) =>
      relatedIds.has(request.assignment_id),
    );
    return linked ? pendingRequestToSummary(linked) : null;
  }

  function feinplanungApprovalDialogProps(assignment: PlanungAssignment | null) {
    const openRequest = resolveOpenFeinplanungRequest(assignment);
    if (!openRequest) return {};
    const requestId = openRequest.id;
    return {
      openFeinplanungRequest: openRequest,
      onApproveFeinplanungRequest: () =>
        assignment && void handleApproveOpenFeinplanungRequest(assignment),
      onRejectFeinplanungRequest: () =>
        assignment && void handleRejectOpenFeinplanungRequest(assignment),
      reviewingFeinplanungRequest: reviewingFeinplanungRequest != null,
      approvingFeinplanungRequest:
        reviewingFeinplanungRequest?.id === requestId &&
        reviewingFeinplanungRequest.action === "approve",
      rejectingFeinplanungRequest:
        reviewingFeinplanungRequest?.id === requestId &&
        reviewingFeinplanungRequest.action === "reject",
    };
  }

  function monteurProposalApprovalDialogProps(assignment: PlanungAssignment | null) {
    if (!assignment || !canApproveMonteurProposals) return {};
    if (assignment.monteur_proposal_status !== "pending") return {};
    const assignmentId = assignment.id;
    return {
      onApproveMonteurProposal: () => void handleApproveMonteurProposal(assignment),
      onRejectMonteurProposal: () => void handleRejectMonteurProposal(assignment),
      reviewingMonteurProposal: reviewingMonteurProposal != null,
      approvingMonteurProposal:
        reviewingMonteurProposal?.id === assignmentId &&
        reviewingMonteurProposal.action === "approve",
      rejectingMonteurProposal:
        reviewingMonteurProposal?.id === assignmentId &&
        reviewingMonteurProposal.action === "reject",
    };
  }

  function openFeinplanung(assignment: PlanungAssignment) {
    setFinePlanning({ open: true, assignment });
  }

  function openAssignmentLinks(assignment: PlanungAssignment) {
    setLinkPickState(null);
    setAssignmentLinks({ open: true, assignment });
  }

  function startLinkPickMode() {
    const primary = assignmentLinks.assignment;
    if (!primary) return;
    const excluded = new Set<number>([
      primary.id,
      ...(primary.linked_assignment_ids ?? []),
    ]);
    setLinkPickState((prev) => ({
      primaryId: primary.id,
      selectedIds: prev?.primaryId === primary.id ? prev.selectedIds : [],
      excludedIds: [...excluded],
      boardPickActive: true,
    }));
    setAssignmentLinks((prev) => ({ ...prev, open: false }));
  }

  function endBoardLinkPick() {
    setLinkPickState((prev) => (prev ? { ...prev, boardPickActive: false } : prev));
    setAssignmentLinks((prev) => ({ ...prev, open: true }));
  }

  function clearLinkPickState() {
    setLinkPickState(null);
  }

  function clearLinkPickSelection() {
    setLinkPickState((prev) => (prev ? { ...prev, selectedIds: [] } : prev));
  }

  function toggleLinkPickAssignment(assignmentId: number) {
    setLinkPickState((prev) => {
      if (!prev || assignmentId === prev.primaryId) return prev;
      if (prev.excludedIds.includes(assignmentId)) return prev;
      const selected = new Set(prev.selectedIds);
      if (selected.has(assignmentId)) selected.delete(assignmentId);
      else selected.add(assignmentId);
      return { ...prev, selectedIds: [...selected] };
    });
  }

  const linkPickBoardActive = linkPickState?.boardPickActive ?? false;
  const linkPickReviewMode =
    assignmentLinks.open && linkPickState != null && !linkPickBoardActive;

  async function toggleAssignmentSeries(assignment: PlanungAssignment) {
    const next = !assignment.is_series;
    const ok = await confirm({
      title: next ? "Als Serie führen" : "Serie aufheben",
      confirmLabel: next ? "Als Serie führen" : "Serie aufheben",
      variant: "warning",
      message: next
        ? "Die Einsätze bleiben verbunden. Die Feinplanung zeigt zuerst diesen Einsatz, die anderen Termine der Serie können Sie dort öffnen. Projekte gelten nur für den jeweiligen Einsatz und stapeln sich nicht auf vergangenen oder späteren Terminen."
        : "Die Einsätze bleiben verknüpft. Beim nächsten Speichern gelten Projekte und Feinplanung wieder für alle Termine und können dabei überschrieben werden.",
    });
    if (!ok) return;
    try {
      await updateAssignmentSeriesMode(assignment.id, next);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Serie konnte nicht geändert werden.");
    }
  }

  async function unlinkAssignmentFromGroup(assignment: PlanungAssignment) {
    const ok = await confirm({
      title: "Verknüpfung lösen",
      message:
        "Diesen Einsatz von der Verknüpfung lösen? Inhalt und Feinplanung werden nicht mehr automatisch synchronisiert.",
      confirmLabel: "Lösen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await unlinkAssignment(assignment.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verknüpfung konnte nicht gelöst werden.");
    }
  }
  const canApproveAbsences = (board?.can_approve_absences ?? false) && !isHistorical;
  const canViewPendingAbsences = board?.can_view_pending_absences ?? false;
  const rows = board?.rows ?? [];

  const hitTestPlan = useCallback(
    (clientX: number, clientY: number): { rowId: number; dayIndex: number } | null => {
      for (const [rowId, el] of trackRefs.current) {
        const rect = el.getBoundingClientRect();
        if (
          clientX < rect.left ||
          clientX >= rect.right ||
          clientY < rect.top ||
          clientY >= rect.bottom
        ) {
          continue;
        }
        const dayIndex = Math.min(
          Math.max(Math.floor((clientX - rect.left) / DAY_W), 0),
          lastIdx,
        );
        return { rowId, dayIndex };
      }
      return null;
    },
    [DAY_W, lastIdx],
  );

  const placementAt = useCallback(
    (clientX: number, clientY: number, item: PlanungAblageItem): AblagePlacement | null => {
      const hit = hitTestPlan(clientX, clientY);
      const anchor = hit ? days[hit.dayIndex] : undefined;
      if (!hit || !anchor) return null;
      return placeAblage(rows, hit.rowId, anchor, item.person_count, item.day_count);
    },
    [days, hitTestPlan, rows],
  );

  const updateAblageDrag = useCallback(
    (item: PlanungAblageItem, clientX: number, clientY: number) => {
      const placement = placementAt(clientX, clientY, item);
      setAblageDrag({
        item,
        placement,
        hint: ablageDragHint(item.person_count, item.day_count, placement),
      });
    },
    [placementAt],
  );

  const commitAblageDrag = useCallback(
    async (item: PlanungAblageItem, clientX: number, clientY: number) => {
      const placement = placementAt(clientX, clientY, item);
      setAblageDrag(null);
      suppressAblageClickRef.current = true;
      if (!placement) {
        setAblageStatus(null);
        return;
      }
      if (!placement.complete) {
        setAblageStatus(
          `Nur ${placement.rowIds.length} von ${item.person_count} Mitarbeiterzeilen liegen unter diesem Punkt.`,
        );
        return;
      }
      setAblageStatus(null);
      setSchedulingAblage(true);
      try {
        await scheduleAblage(item.id, {
          row_ids: placement.rowIds,
          date_from: placement.dateFrom,
          date_to: placement.dateTo,
        });
        setAblageRefresh((current) => current + 1);
        const fromDate = parseIso(placement.dateFrom);
        const toDate = parseIso(placement.dateTo);
        const nextFrom = fromDate < rangeFrom ? addDays(fromDate, -7) : rangeFrom;
        const nextTo = toDate > rangeTo ? addDays(toDate, 7) : rangeTo;
        if (nextFrom.getTime() !== rangeFrom.getTime()) setRangeFrom(nextFrom);
        if (nextTo.getTime() !== rangeTo.getTime()) setRangeTo(nextTo);
        const data = await fetchBoardForRange(nextFrom, nextTo);
        setBoard(data);
        syncOpenAssignmentFromBoard(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Montage konnte nicht eingeplant werden.";
        setError(message);
        setAblageStatus(message);
      } finally {
        setSchedulingAblage(false);
      }
    },
    [fetchBoardForRange, placementAt, rangeFrom, rangeTo, syncOpenAssignmentFromBoard],
  );

  useEffect(() => {
    if (!ablageDrag) return;
    document.body.classList.add("mp-ablage-drag");
    return () => document.body.classList.remove("mp-ablage-drag");
  }, [ablageDrag]);

  const ablageGhost = useMemo(() => {
    const placement = ablageDrag?.placement;
    if (!placement || placement.rowIds.length === 0) return null;
    const startIdx = days.findIndex((day) => toIso(day) === placement.dateFrom);
    if (startIdx < 0) return null;
    let endIdx = days.findIndex((day) => toIso(day) === placement.dateTo);
    if (endIdx < startIdx) endIdx = startIdx;
    return {
      startIdx,
      span: Math.max(1, endIdx - startIdx + 1),
      rowIds: new Set(placement.rowIds),
      title: ablageDrag.item.title,
      color: ablageDrag.item.color || DEFAULT_PLANUNG_COLOR,
      complete: placement.complete,
    };
  }, [ablageDrag, days]);
  const employeeRows = useMemo(
    () => rows.filter((row) => row.kind === "employee"),
    [rows],
  );
  const employeeLogins = useMemo(
    () =>
      rows
        .filter((row) => row.kind === "employee" && row.employee_login)
        .map((row) => row.employee_login as string),
    [rows],
  );
  const {
    hoursByLoginDate: dailyHoursByLoginDate,
    paidHoursByLoginDate: dailyPaidHoursByLoginDate,
    overtimeByLogin,
  } = useMontageplanTimesOverlay(
    days,
    employeeLogins,
    Boolean(board) && !isHistorical && canViewWorkHours,
  );
  const [showPlannedAssignments, setShowPlannedAssignments] = useState(true);
  const [showBookedAssignments, setShowBookedAssignments] = useState(false);
  const [bookedColorMode, setBookedColorMode] = useState<BookedColorMode>("status");
  const isBookedDayPlanned = useMemo(() => {
    const rowLogin = new Map<number, string>();
    for (const row of rows) {
      if (row.kind === "employee" && row.employee_login) {
        rowLogin.set(row.id, row.employee_login.trim().toLowerCase());
      }
    }
    const rangesByLogin = new Map<string, { from: string; to: string; orderIds: Set<number> }[]>();
    for (const assignment of board?.assignments ?? []) {
      if (assignment.is_company_event) continue;
      const login = rowLogin.get(assignment.row_id);
      if (!login) continue;
      const orderIds = new Set(
        assignment.orders.map((order) => order.order_id).filter((id) => Number.isFinite(id)),
      );
      if (orderIds.size === 0) continue;
      const list = rangesByLogin.get(login) ?? [];
      list.push({
        from: assignment.date_from.slice(0, 10),
        to: assignment.date_to.slice(0, 10),
        orderIds,
      });
      rangesByLogin.set(login, list);
    }
    return (login: string, iso: string, auftragId: number) =>
      (rangesByLogin.get(login) ?? []).some(
        (range) => range.orderIds.has(auftragId) && iso >= range.from && iso <= range.to,
      );
  }, [board?.assignments, rows]);
  const bookedSpans = useBookedAssignments(
    days,
    employeeLogins,
    Boolean(board) && !isHistorical && canViewWorkHours && showBookedAssignments,
    isBookedDayPlanned,
  );
  const bookedByLogin = useMemo(() => {
    const map = new Map<string, typeof bookedSpans>();
    for (const span of bookedSpans) {
      const list = map.get(span.userLogin) ?? [];
      list.push(span);
      map.set(span.userLogin, list);
    }
    return map;
  }, [bookedSpans]);
  const [nachunternehmerLogins, setNachunternehmerLogins] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!canViewWorkHours) {
      setNachunternehmerLogins(new Set());
      return;
    }
    let cancelled = false;
    void api
      .get<HeroEmployeeRecord[]>("/hero/employees")
      .then((list) => {
        if (cancelled) return;
        const next = new Set<string>();
        for (const emp of Array.isArray(list) ? list : []) {
          if (!emp.is_nachunternehmer) continue;
          const login = employeeLoginFromRecord(emp);
          if (login) next.add(login);
        }
        setNachunternehmerLogins(next);
      })
      .catch(() => {
        if (!cancelled) setNachunternehmerLogins(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, [canViewWorkHours]);
  const rowIds = useMemo(
    () => rows.filter((r) => r.kind !== "separator").map((r) => r.id),
    [rows],
  );

  const months = useMemo(
    () =>
      groupConsecutive(
        days,
        (d) => `${d.getFullYear()}-${d.getMonth()}`,
        (d) => `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      ),
    [days],
  );

  const weekGroups = useMemo(
    () =>
      groupConsecutive(
        days,
        (d) => `${d.getFullYear()}-${isoWeek(d)}`,
        (d) => `KW ${isoWeek(d)}`,
      ),
    [days],
  );

  const dragPreviewMap = useMemo(() => {
    if (!dragPreview || !board) return null;
    if (activeDragMode !== "move" || dragPreview.detachLinked) {
      return { [dragPreview.id]: dragPreview };
    }
    const primary = board.assignments.find((a) => a.id === dragPreview.id);
    if (!primary) return { [dragPreview.id]: dragPreview };
    const delta = diffDays(
      parseIso(primary.date_from.slice(0, 10)),
      parseIso(dragPreview.dateFrom),
    );
    const map: Record<number, AssignmentDragPreview> = {
      [dragPreview.id]: dragPreview,
    };
    for (const peerId of primary.linked_assignment_ids ?? []) {
      const peer = board.assignments.find((item) => item.id === peerId);
      if (!peer) continue;
      map[peerId] = {
        id: peerId,
        dateFrom: toIso(addDays(parseIso(peer.date_from.slice(0, 10)), delta)),
        dateTo: toIso(addDays(parseIso(peer.date_to.slice(0, 10)), delta)),
        rowId: peer.row_id,
      };
    }
    return map;
  }, [dragPreview, board, activeDragMode]);

  const linkedDragIdSet = useMemo(() => {
    if (!dragPreviewMap) return new Set<number>();
    return new Set(Object.keys(dragPreviewMap).map(Number));
  }, [dragPreviewMap]);

  const assignmentsByRow = useMemo(() => {
    const map = new Map<number, PlanungAssignment[]>();
    for (const a of board?.assignments ?? []) {
      const previewData = dragPreviewMap?.[a.id];
      const preview = previewData
        ? {
            ...a,
            date_from: previewData.dateFrom,
            date_to: previewData.dateTo,
            row_id: previewData.rowId,
          }
        : a;
      const rowId = preview.row_id;
      const arr = map.get(rowId) ?? [];
      arr.push(preview);
      map.set(rowId, arr);
    }
    return map;
  }, [board, dragPreviewMap]);

  const absencesByLogin = useMemo(() => {
    const map = new Map<string, PlanungAbsence[]>();
    for (const absence of board?.absences ?? []) {
      const login = absence.user_login.toLowerCase();
      const arr = map.get(login) ?? [];
      arr.push(absence);
      map.set(login, arr);
    }
    return map;
  }, [board?.absences]);

  /** Nur sichtbare Tageszellen (+ Puffer) rendern – spart DOM bei breiten Ranges. */
  const visibleDayCellRange = useMemo(() => {
    if (days.length === 0) return { start: 0, end: 0 };
    // Vor dem ersten Layout-Messwert alle Zellen rendern (vermeidet leeren Track).
    if (headScroll.trackVisibleWidth <= 0) {
      return { start: 0, end: days.length };
    }
    const start = Math.max(
      0,
      Math.floor(headScroll.scrollLeft / DAY_W) - MP_DAYCELL_BUFFER_DAYS,
    );
    const end = Math.min(
      days.length,
      Math.ceil((headScroll.scrollLeft + headScroll.trackVisibleWidth) / DAY_W) +
        MP_DAYCELL_BUFFER_DAYS,
    );
    return { start, end: Math.max(start, end) };
  }, [DAY_W, days.length, headScroll.scrollLeft, headScroll.trackVisibleWidth]);

  const rowLayouts = useMemo(() => {
    const map = new Map<number, ReturnType<typeof computeRowTrackLayout>>();
    for (const row of rows) {
      if (row.kind === "separator") continue;
      const login = row.kind === "employee" ? row.employee_login?.toLowerCase() : null;
      const bookedLogin = login?.trim() || null;
      map.set(
        row.id,
        computeRowTrackLayout(
          showPlannedAssignments ? (assignmentsByRow.get(row.id) ?? []) : [],
          login ? (absencesByLogin.get(login) ?? []) : [],
          windowFrom,
          lastIdx,
          metrics,
          {
            reserveHoursBand: row.kind === "employee" && canViewWorkHours,
            booked:
              showBookedAssignments && bookedLogin
                ? (bookedByLogin.get(bookedLogin) ?? []).map((span) => ({
                    key: span.key,
                    dateFrom: span.dateFrom,
                    dateTo: span.dateTo,
                  }))
                : undefined,
          },
        ),
      );
    }
    return map;
  }, [
    rows,
    assignmentsByRow,
    showPlannedAssignments,
    absencesByLogin,
    bookedByLogin,
    showBookedAssignments,
    windowFrom,
    lastIdx,
    metrics,
    canViewWorkHours,
  ]);

  const calendarByDay = useMemo(() => {
    const map = new Map<string, PlanungCalendarDay>();
    for (const day of board?.calendar_days ?? []) {
      map.set(day.work_date.slice(0, 10), day);
    }
    return map;
  }, [board?.calendar_days]);

  const legendProps = useMemo(
    () => ({
      clipboard,
      canEdit,
      canViewPendingAbsences,
      showMonteurProposals: canCreateMonteurProposal || canApproveMonteurProposals,
      canCreateMonteurProposal,
      showBookedToggle: canViewWorkHours && !isHistorical,
      showPlannedAssignments,
      onTogglePlannedAssignments: () =>
        setShowPlannedAssignments((on) => {
          const next = !on;
          if (!next && canViewWorkHours && !isHistorical) setShowBookedAssignments(true);
          return next;
        }),
      showBookedAssignments,
      bookedColorMode,
      onToggleBookedAssignments: () => setShowBookedAssignments((on) => !on),
      onBookedColorModeChange: setBookedColorMode,
      pasteLinked,
      onTogglePasteLinked: () =>
        setPasteLinked((on) => {
          const next = !on;
          writePasteLinkedPreference(next);
          return next;
        }),
    }),
    [
      clipboard,
      canEdit,
      pasteLinked,
      canViewPendingAbsences,
      canCreateMonteurProposal,
      canApproveMonteurProposals,
      canViewWorkHours,
      isHistorical,
      showPlannedAssignments,
      showBookedAssignments,
      bookedColorMode,
    ],
  );

  const unavailabilities = board?.unavailabilities ?? [];

  useLayoutEffect(() => {
    const legend = legendRef.current;
    const shell = legend?.closest<HTMLElement>(".mp-planning-shell");
    if (!legend || !shell) return;

    const syncLegendHeight = () => {
      shell.style.setProperty("--mp-legend-height", `${legend.offsetHeight}px`);
    };

    syncLegendHeight();
    const observer = new ResizeObserver(syncLegendHeight);
    observer.observe(legend);
    return () => observer.disconnect();
  }, [legendProps, isCompact, rows.length]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const inner = headTrackScrollRef.current;
    if (!scroll || !inner) return;

    let raf = 0;

    const syncHeadScroll = () => {
      inner.style.transform = `translateX(${-scroll.scrollLeft}px)`;
      setHeadScroll(getTrackMetrics(scroll));
      checkExtendEdges(scroll);
    };

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncHeadScroll);
    };

    syncHeadScroll();
    scroll.addEventListener("scroll", onScroll, { passive: true });
    const resizeObserver = new ResizeObserver(onScroll);
    resizeObserver.observe(scroll);
    return () => {
      scroll.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [
    fromIso,
    toIsoStr,
    isCompact,
    totalWidth,
    rows.length,
    getTrackMetrics,
    checkExtendEdges,
  ]);

  const headRowProps = {
    labelW: LABEL_W,
    totalWidth,
    dayW: DAY_W,
    months,
    weekGroups,
    days,
    calendarByDay,
    today,
    isCompact,
    headScrollLeft: headScroll.scrollLeft,
    headTrackVisibleWidth: headScroll.trackVisibleWidth,
    trackScrollRef: headTrackScrollRef,
  };

  const commitAssignmentUpdate = useCallback(
    async (preview: AssignmentDragPreview, syncLinked: boolean) => {
      const primary = board?.assignments.find((a) => a.id === preview.id);
      if (!primary || !board) return;
      suppressBarClickRef.current = true;

      const updates: AssignmentDragPreview[] = [preview];
      if (syncLinked && primary.linked_assignment_ids?.length) {
        const delta = diffDays(
          parseIso(primary.date_from.slice(0, 10)),
          parseIso(preview.dateFrom),
        );
        for (const peerId of primary.linked_assignment_ids) {
          const peer = board.assignments.find((a) => a.id === peerId);
          if (!peer) continue;
          updates.push({
            id: peerId,
            dateFrom: toIso(addDays(parseIso(peer.date_from.slice(0, 10)), delta)),
            dateTo: toIso(addDays(parseIso(peer.date_to.slice(0, 10)), delta)),
            rowId: peer.row_id,
          });
        }
      }

      const originals = new Map(
        updates
          .map((u) => board.assignments.find((a) => a.id === u.id))
          .filter((a): a is PlanungAssignment => a != null)
          .map((a) => [a.id, a] as const),
      );

      setBoard((prev) =>
        prev
          ? {
              ...prev,
              assignments: prev.assignments.map((a) => {
                const update = updates.find((u) => u.id === a.id);
                if (!update) return a;
                return {
                  ...a,
                  row_id: update.rowId,
                  date_from: update.dateFrom,
                  date_to: update.dateTo,
                };
              }),
            }
          : prev,
      );

      try {
        for (const update of updates) {
          const original = originals.get(update.id);
          if (!original) continue;
          await updateAssignment(update.id, {
            row_id: update.rowId,
            title: original.title,
            color: original.color,
            date_from: update.dateFrom,
            date_to: update.dateTo,
            is_fixed: original.is_fixed,
            is_blocker: original.is_blocker ?? false,
            is_company_event: original.is_company_event ?? false,
            orders: original.orders ?? [],
            note: original.note,
          });
        }
        load();
      } catch (err) {
        setBoard((prev) =>
          prev
            ? {
                ...prev,
                assignments: prev.assignments.map((a) => {
                  const original = originals.get(a.id);
                  return original ?? a;
                }),
              }
            : prev,
        );
        setError(err instanceof Error ? err.message : "Eintrag konnte nicht verschoben werden.");
      }
    },
    [board, load],
  );

  const clearActiveDrag = useCallback(() => {
    setActiveDrag(null);
    setActiveDragMode(null);
  }, []);

  const handleDragCommit = useCallback(
    async (preview: AssignmentDragPreview) => {
      suppressBarClickRef.current = true;
      const original = board?.assignments.find((a) => a.id === preview.id);
      const draggedRow = board?.rows.find((row) => row.id === original?.row_id);
      const dropRow = board?.rows.find((row) => row.id === preview.rowId);
      if (draggedRow?.system_key === "meros" || dropRow?.system_key === "meros") {
        setError("Auf der Meros-Zeile sind keine manuellen Einsätze möglich.");
        return;
      }
      const syncLinked =
        activeDragMode === "move" &&
        !preview.detachLinked &&
        (original?.linked_assignment_ids?.length ?? 0) > 0;
      const idsToCheck = syncLinked
        ? [preview.id, ...(original?.linked_assignment_ids ?? [])]
        : [preview.id];
      const fixedWithStartChange = (() => {
        if (!board || !original) return null;
        const dayDelta = diffDays(
          parseIso(original.date_from.slice(0, 10)),
          parseIso(preview.dateFrom),
        );
        for (const id of idsToCheck) {
          const assignment = board.assignments.find((a) => a.id === id);
          if (!assignment?.is_fixed) continue;
          const newFrom =
            id === preview.id
              ? preview.dateFrom
              : toIso(addDays(parseIso(assignment.date_from.slice(0, 10)), dayDelta));
          if (newFrom !== assignment.date_from.slice(0, 10)) {
            return assignment;
          }
        }
        return null;
      })();
      if (fixedWithStartChange) {
        const ok = await confirm({
          title: "Fixer Termin",
          message: (
            <>
              <strong>{fixedWithStartChange.title}</strong> ist als fix markiert.
              <span className="mt-2 block text-label">
                Das Startdatum würde sich ändern. Möchten Sie das trotzdem durchführen?
              </span>
            </>
          ),
          confirmLabel: "Startdatum ändern",
          variant: "warning",
        });
        if (!ok) return;
      }
      const targetRow = board?.rows.find((r) => r.id === preview.rowId);
      if (targetRow?.kind === "employee" && targetRow.employee_login) {
        const conflicts = findAbsenceConflicts(
          absencesByLogin.get(targetRow.employee_login.toLowerCase()) ?? [],
          preview.dateFrom,
          preview.dateTo,
        );
        if (conflicts.length > 0) {
          const label = targetRow.label || targetRow.employee_login || "den Mitarbeiter";
          const ok = await confirm({
            title: "Abwesenheits-Konflikt",
            message: (
              <>
                {formatAbsenceConflictMessage(conflicts, label)}
                <span className="mt-2 block text-label">Möchten Sie den Einsatz trotzdem verschieben?</span>
              </>
            ),
            confirmLabel: "Trotzdem verschieben",
            variant: "warning",
          });
          if (!ok) return;
        }
      }
      await commitAssignmentUpdate(preview, syncLinked);
    },
    [absencesByLogin, board, activeDragMode, commitAssignmentUpdate, confirm],
  );

  const parkAssignment = useCallback(
    async (assignment: PlanungAssignment) => {
      if (assignment.is_company_event) {
        setAblageStatus("Betriebsveranstaltungen bleiben in der Planung.");
        return;
      }
      if (assignment.monteur_proposal_status === "pending") {
        setAblageStatus("Offene Monteursvorschläge können nicht in die Ablage.");
        return;
      }
      const linkedSameSpan =
        !assignment.is_series && (assignment.linked_assignment_ids?.length ?? 0) > 0;
      const ok = await confirm({
        title: "In die Ablage verschieben",
        message: linkedSameSpan
          ? `„${assignment.title}“ und die verknüpften Einsätze im selben Zeitraum aus dem Plan nehmen? Die Termine verschwinden aus der Planung. Die Tagesfeinplanung daran wird entfernt.`
          : `„${assignment.title}“ aus dem Plan nehmen und in die Ablage legen? Der Termin verschwindet aus der Planung. Die Tagesfeinplanung daran wird entfernt.`,
        confirmLabel: "In die Ablage",
        variant: "warning",
      });
      if (!ok) return;
      setAblageStatus(null);
      try {
        await parkAssignmentInAblage(assignment.id);
        setAblageRefresh((current) => current + 1);
        load();
      } catch (err) {
        setAblageStatus(
          err instanceof Error ? err.message : "Einsatz konnte nicht in die Ablage verschoben werden.",
        );
      }
    },
    [confirm, load],
  );

  useAssignmentDrag({
    active: activeDrag,
    days,
    dayWidth: DAY_W,
    trackRefs,
    rowIds,
    onPreview: setDragPreview,
    onCommit: handleDragCommit,
    onClear: clearActiveDrag,
    parkElementRef: ablageDropRef,
    onParkHover: setAblageParkHover,
    onPark: parkAssignment,
  });

  const { onPanPointerDown, suppressPanClickRef } = useScrollPan({
    scrollRef,
    enabled: !activeDrag && !extending,
  });

  function rowAbsences(row: PlanungRow): PlanungAbsence[] {
    if (row.kind !== "employee" || !row.employee_login) return [];
    return absencesByLogin.get(row.employee_login.toLowerCase()) ?? [];
  }

  function openNewMonteurProposal(row: PlanungRow, dayIndex: number) {
    const safe = Math.min(Math.max(dayIndex, 0), lastIdx);
    setMonteurProposalDialog({
      open: true,
      row,
      assignment: null,
      defaultDate: toIso(days[safe]!),
    });
  }

  function openNew(row: PlanungRow, dayIndex: number) {
    if (!canEdit || activeDrag) return;
    const safe = Math.min(Math.max(dayIndex, 0), lastIdx);
    setDialog({ open: true, row, assignment: null, defaultDate: toIso(days[safe]!) });
  }

  function openEdit(row: PlanungRow, assignment: PlanungAssignment) {
    if (suppressBarClickRef.current) {
      suppressBarClickRef.current = false;
      return;
    }
    if (assignment.is_company_event) {
      setCompanyEventDialog({
        open: true,
        defaultDate: assignment.date_from.slice(0, 10),
        preselectedRowId: null,
        assignment,
      });
      return;
    }
    if (shouldUseMonteurProposalDialog(assignment)) {
      setMonteurProposalDialog({
        open: true,
        row,
        assignment,
        defaultDate: assignment.date_from.slice(0, 10),
      });
      return;
    }
    if (!canEditAssignment(assignment)) {
      openView(row, assignment);
      return;
    }
    setDialog({ open: true, row, assignment, defaultDate: assignment.date_from.slice(0, 10) });
    if (canApproveFeinplanungRequests) {
      refreshPendingFeinplanungRequests();
    }
    if (canApproveMonteurProposals) {
      refreshPendingMonteurProposals();
    }
  }

  function openAssignmentHistory(assignment: PlanungAssignment) {
    const entityIds = Array.from(
      new Set(
        [assignment.id, ...(assignment.linked_assignment_ids ?? [])].filter((id) => id > 0),
      ),
    );
    setHistoryScope({ title: assignment.title, entityIds });
    setHistoryOpen(true);
  }

  function openView(row: PlanungRow, assignment: PlanungAssignment) {
    setViewDialog({ open: true, assignment, row });
    if (canApproveFeinplanungRequests) {
      refreshPendingFeinplanungRequests();
    }
  }

  function copyAssignment(assignment: PlanungAssignment) {
    setClipboard(assignmentToClipboard(assignment));
  }

  function nonWorkWeekdaysForRow(row: PlanungRow): number[] | null | undefined {
    if (row.kind === "employee") return row.non_work_weekdays;
    return [6, 7];
  }

  async function pasteAssignment(row: PlanungRow, dayIndex: number, link: boolean) {
    if (!clipboard) return;
    const safeIdx = Math.min(Math.max(dayIndex, 0), lastIdx);
    const anchorIso = toIso(days[safeIdx] ?? today);
    try {
      await createAssignment(
        clipboardToAssignmentInput(clipboard, row.id, anchorIso, nonWorkWeekdaysForRow(row), {
          link,
        }),
      );
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Eintrag konnte nicht eingefügt werden.");
    }
  }

  function clearPlanFocusHighlight() {
    setPlanFocusHighlightIds(new Set());
  }

  const applyPlanFocus = useCallback(() => {
    const focus = pendingPlanFocusRef.current;
    if (!focus) return;
    if (!board?.assignments.some((a) => a.id === focus.assignmentId)) return;
    pendingPlanFocusRef.current = null;

    setPlanFocusHighlightIds(new Set([focus.assignmentId]));
    setLinkHighlightIds(new Set());

    const dayIdx = days.findIndex((d) => toIso(d) === focus.dateFrom.slice(0, 10));
    const el = scrollRef.current;
    if (el && dayIdx >= 0) {
      const dayCenter = dayIdx * DAY_W + DAY_W / 2;
      const visibleTrackWidth = Math.max(0, el.clientWidth - LABEL_W);
      el.scrollLeft = Math.max(0, dayCenter - visibleTrackWidth / 2);
      setHeadScroll(getTrackMetrics(el));
    }

    window.requestAnimationFrame(() => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const barEl = scrollEl.querySelector<HTMLElement>(
        `[data-mp-assignment-id="${focus.assignmentId}"]`,
      );
      barEl?.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
    });
  }, [DAY_W, LABEL_W, board?.assignments, days, getTrackMetrics]);

  const focusAssignmentInPlan = useCallback(
    (focus: { assignmentId: number; dateFrom: string; dateTo: string; rowId: number }) => {
      pendingPlanFocusRef.current = {
        assignmentId: focus.assignmentId,
        dateFrom: focus.dateFrom,
        rowId: focus.rowId,
      };

      const from = parseIso(focus.dateFrom.slice(0, 10));
      const to = parseIso((focus.dateTo || focus.dateFrom).slice(0, 10));
      const padDays = 14;
      const needFrom = addDays(from, -padDays);
      const needTo = addDays(to, padDays);
      const onBoard = board?.assignments.some((a) => a.id === focus.assignmentId) ?? false;

      if (needFrom < rangeFrom || needTo > rangeTo || !onBoard) {
        setLoading(true);
        setError(null);
        void fetchBoardForRange(needFrom, needTo)
          .then((data) => {
            setBoard(data);
            setRangeFrom(needFrom);
            setRangeTo(needTo);
            if (
              !data.assignments.some((a) => a.id === focus.assignmentId) &&
              pendingPlanFocusRef.current?.assignmentId === focus.assignmentId
            ) {
              pendingPlanFocusRef.current = null;
            }
          })
          .catch((err) =>
            setError(
              err instanceof Error ? err.message : "Montageplanung konnte nicht geladen werden.",
            ),
          )
          .finally(() => setLoading(false));
        return;
      }

      applyPlanFocus();
    },
    [applyPlanFocus, board?.assignments, fetchBoardForRange, rangeFrom, rangeTo],
  );

  const highlightRequestInPlan = useCallback(
    (request: PlanungFeinplanungRequest) => {
      setFeinplanungRequestsOpen(false);
      focusAssignmentInPlan({
        assignmentId: request.assignment_id,
        dateFrom: request.assignment_date_from,
        dateTo: request.assignment_date_to,
        rowId:
          request.assignment_row_id ??
          board?.assignments.find((a) => a.id === request.assignment_id)?.row_id ??
          0,
      });
    },
    [board?.assignments, focusAssignmentInPlan],
  );

  const highlightMonteurProposalInPlan = useCallback(
    (request: PlanungMonteurProposalListItem) => {
      setMonteurProposalsOpen(false);
      focusAssignmentInPlan({
        assignmentId: request.id,
        dateFrom: request.date_from,
        dateTo: request.date_to,
        rowId: request.row_id,
      });
    },
    [focusAssignmentInPlan],
  );

  const focusAssignmentInPlanRef = useRef(focusAssignmentInPlan);
  focusAssignmentInPlanRef.current = focusAssignmentInPlan;
  const resetToTodayViewRef = useRef(resetToTodayView);
  resetToTodayViewRef.current = resetToTodayView;

  useEffect(() => {
    const parsed = parseMontageplanHighlight(
      highlightQuery,
      highlightFromQuery,
      highlightToQuery,
    );
    if (parsed) {
      suppressTodayResetRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlight");
          next.delete("from");
          next.delete("to");
          return next;
        },
        { replace: true },
      );
      focusAssignmentInPlanRef.current(parsed);
      return;
    }
    if (suppressTodayResetRef.current) {
      suppressTodayResetRef.current = false;
      return;
    }
    resetToTodayViewRef.current();
  }, [highlightFromQuery, highlightQuery, highlightToQuery, historyLogId, setSearchParams]);

  useLayoutEffect(() => {
    if (!pendingPlanFocusRef.current) return;
    applyPlanFocus();
  }, [applyPlanFocus, board, days.length, rangeFrom, rangeTo]);

  const refreshPendingMonteurProposals = useCallback(() => {
    void fetchMonteurProposalsOpenCount()
      .then(setMonteurProposalsCount)
      .catch(() => setMonteurProposalsCount(0));
  }, []);

  const refreshPendingFeinplanungRequests = useCallback(() => {
    void fetchFeinplanungRequestsOpenCount()
      .then(setFeinplanungRequestsCount)
      .catch(() => setFeinplanungRequestsCount(0));
    void fetchPendingFeinplanungRequests()
      .then(setPendingFeinplanungRequests)
      .catch(() => setPendingFeinplanungRequests([]));
  }, []);

  useEffect(() => {
    const canApprove = (board?.can_approve_monteur_proposals ?? false) && !isHistorical;
    if (!canApprove) {
      setMonteurProposalsCount(0);
      return;
    }
    refreshPendingMonteurProposals();
    window.addEventListener("montageplanung_monteur_proposals_changed", refreshPendingMonteurProposals);
    return () => {
      window.removeEventListener(
        "montageplanung_monteur_proposals_changed",
        refreshPendingMonteurProposals,
      );
    };
  }, [board?.can_approve_monteur_proposals, isHistorical, monteurProposalsOpen, refreshPendingMonteurProposals]);

  useEffect(() => {
    const canApprove = (board?.can_approve_feinplanung_requests ?? false) && !isHistorical;
    if (!canApprove) {
      setFeinplanungRequestsCount(0);
      setPendingFeinplanungRequests([]);
      return;
    }
    refreshPendingFeinplanungRequests();
    window.addEventListener("montageplanung_feinplanung_requests_changed", refreshPendingFeinplanungRequests);
    return () => {
      window.removeEventListener("montageplanung_feinplanung_requests_changed", refreshPendingFeinplanungRequests);
    };
  }, [board?.can_approve_feinplanung_requests, isHistorical, feinplanungRequestsOpen, refreshPendingFeinplanungRequests]);

  function highlightLinkedGroup(assignmentId: number) {
    const assignment = board?.assignments.find((a) => a.id === assignmentId);
    if (!assignment?.linked_assignment_ids?.length) {
      setLinkHighlightIds(new Set());
      return;
    }
    setLinkHighlightIds(
      new Set([assignment.id, ...assignment.linked_assignment_ids]),
    );
  }

  function clearLinkHighlight() {
    setLinkHighlightIds(new Set());
  }

  function startAssignmentDrag(
    assignment: PlanungAssignment,
    row: PlanungRow,
    mode: AssignmentDragMode,
    event: React.MouseEvent | React.PointerEvent,
  ) {
    if (!canEdit) return;
    clearLinkHighlight();
    setActiveDragMode(mode);
    setActiveDrag({
      assignment,
      mode,
      originFrom: assignment.date_from.slice(0, 10),
      originTo: assignment.date_to.slice(0, 10),
      originRowId: row.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
    });
  }

  function handleTrackClick(row: PlanungRow, e: React.MouseEvent<HTMLDivElement>) {
    if (row.system_key === "meros") return;
    if (suppressAblageClickRef.current) {
      suppressAblageClickRef.current = false;
      return;
    }
    if (activeDrag || ablageDrag) return;
    if (suppressPanClickRef.current) {
      suppressPanClickRef.current = false;
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const idx = Math.floor((e.clientX - rect.left) / DAY_W);
    if (monteurCanProposeOnRow(row)) {
      openNewMonteurProposal(row, idx);
      return;
    }
    if (!canEdit) return;
    openNew(row, idx);
  }

  function dayIndexFromTrackEvent(
    track: HTMLDivElement,
    clientX: number,
  ): number {
    const rect = track.getBoundingClientRect();
    return Math.min(Math.max(Math.floor((clientX - rect.left) / DAY_W), 0), lastIdx);
  }

  function openContextMenu(state: ContextMenuState) {
    setContextMenu(state);
  }

  function openUnavailabilityDialog(
    row: PlanungRow,
    dayIndex: number,
    existing: PlanungUnavailability | null,
  ) {
    const dayIso = toIso(days[dayIndex] ?? today);
    const clicked = defaultUnavailableDay(dayIso);
    setUnavailDialog({
      open: true,
      row,
      existing,
      defaultFrom: existing?.date_from.slice(0, 10) ?? clicked.date_from,
      defaultTo: existing?.date_to.slice(0, 10) ?? clicked.date_to,
    });
  }

  async function removeUnavailability(block: PlanungUnavailability) {
    const ok = await confirm({
      title: "Nicht verfügbar entfernen",
      message: "Diesen Zeitraum aus der Planung entfernen?",
      confirmLabel: "Entfernen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteUnavailability(block.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Eintrag konnte nicht entfernt werden.");
    }
  }

  function openCompanyEventDialog(row: PlanungRow, dayIndex: number) {
    const dayIso = toIso(days[dayIndex] ?? today);
    setCompanyEventDialog({
      open: true,
      defaultDate: dayIso,
      preselectedRowId: row.kind === "employee" ? row.id : null,
      assignment: null,
    });
  }

  function handleTrackContextMenu(row: PlanungRow, e: React.MouseEvent<HTMLDivElement>) {
    if (row.system_key === "meros") {
      e.preventDefault();
      return;
    }
    if (activeDrag) return;
    const monteurCanPropose = monteurCanProposeOnRow(row);
    if (!canEdit && !monteurCanPropose) return;
    e.preventDefault();
    const idx = dayIndexFromTrackEvent(e.currentTarget, e.clientX);
    if (monteurCanPropose && !canEdit) {
      const forSelf = row.id === board?.viewer_row_id;
      openContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: [
          {
            id: "propose",
            label:
              row.kind === "resource"
                ? `${rowLabelFull(row)} beantragen…`
                : forSelf
                  ? "Termin beantragen…"
                  : `Termin für ${rowLabelFull(row)} beantragen…`,
            onClick: () => openNewMonteurProposal(row, idx),
          },
        ],
      });
      return;
    }
    const dayIso = toIso(days[idx] ?? today);
    const hitUnavail =
      row.kind === "employee" ? findUnavailabilityOnDay(row.id, dayIso, unavailabilities) : undefined;
    const items: ContextMenuState["items"] = [];
    if (clipboard) {
      const pasteLinkedItem: ContextMenuState["items"][number] = {
        id: "paste-linked",
        label: "Einfügen und verknüpfen",
        onClick: () => void pasteAssignment(row, idx, true),
      };
      const pastePlainItem: ContextMenuState["items"][number] = {
        id: "paste-plain",
        label: "Einfügen ohne Verknüpfung",
        onClick: () => void pasteAssignment(row, idx, false),
      };
      items.push(pasteLinked ? pasteLinkedItem : pastePlainItem);
      items.push(pasteLinked ? pastePlainItem : pasteLinkedItem);
    }
    items.push({
      id: "insert",
      label: "Neuer Eintrag…",
      onClick: () => openNew(row, idx),
    });
    items.push({
      id: "company-event",
      label: "Betriebsveranstaltung anlegen…",
      onClick: () => openCompanyEventDialog(row, idx),
    });
    if (row.kind === "employee") {
      items.push({
        id: "add-unavail",
        label: "Nicht verfügbar eintragen…",
        onClick: () => openUnavailabilityDialog(row, idx, null),
      });
      if (hitUnavail) {
        items.push({
          id: "edit-unavail",
          label: "Nicht verfügbar bearbeiten…",
          onClick: () => openUnavailabilityDialog(row, idx, hitUnavail),
        });
        items.push({
          id: "delete-unavail",
          label: "Nicht verfügbar entfernen",
          destructive: true,
          onClick: () => void removeUnavailability(hitUnavail),
        });
      }
    }
    openContextMenu({ x: e.clientX, y: e.clientY, items });
  }

  function handleAssignmentContextMenu(
    row: PlanungRow,
    assignment: PlanungAssignment,
    e: React.MouseEvent,
  ) {
    if (activeDrag) return;
    if (row.system_key === "meros") {
      e.preventDefault();
      return;
    }
    const editable = canEditAssignment(assignment);
    const feinplanungEditable = canEditFeinplanung(assignment);
    const requestable = canRequestFeinplanung(assignment);
    const requestPending = feinplanungRequestPending(assignment);
    e.preventDefault();
    const items: ContextMenuState["items"] = [
      {
        id: "view",
        label: "Termin anzeigen",
        onClick: () => openView(row, assignment),
      },
      {
        id: "history",
        label: "Änderungsverlauf",
        onClick: () => openAssignmentHistory(assignment),
      },
    ];
    if (feinplanungEditable) {
      items.push({
        id: "feinplanung",
        label: "Feinplanung bearbeiten",
        onClick: () => openFeinplanung(assignment),
      });
    }
    if (requestable) {
      items.push({
        id: "feinplanung-request",
        label: "Feinplanung beantragen",
        onClick: () => void handleRequestFeinplanung(assignment),
      });
    }
    if (requestPending) {
      items.push({
        id: "feinplanung-request-pending",
        label: "Feinplanung beantragt (ausstehend)",
        disabled: true,
        onClick: () => {},
      });
    }
    if (editable) {
      items.push(
        {
          id: "copy",
          label: "Kopieren",
          onClick: () => copyAssignment(assignment),
        },
        {
          id: "links",
          label: "Verknüpfungen bearbeiten…",
          onClick: () => openAssignmentLinks(assignment),
        },
      );
      if ((assignment.linked_assignment_ids?.length ?? 0) > 0 || assignment.link_group_id) {
        if (!assignment.is_company_event) {
          items.push({
            id: "series",
            label: assignment.is_series ? "Serie aufheben" : "Als Serie führen",
            onClick: () => void toggleAssignmentSeries(assignment),
          });
        }
        items.push({
          id: "unlink",
          label: "Verknüpfung lösen",
          onClick: () => void unlinkAssignmentFromGroup(assignment),
        });
      }
      items.push(
        {
          id: "edit",
          label: "Bearbeiten",
          onClick: () => openEdit(row, assignment),
        },
      );
      if (
        canViewAblage &&
        !assignment.is_company_event &&
        assignment.monteur_proposal_status !== "pending"
      ) {
        items.push({
          id: "park",
          label: "In die Ablage",
          onClick: () => void parkAssignment(assignment),
        });
      }
      items.push(
        {
          id: "delete",
          label: "Löschen",
          destructive: true,
          onClick: () => void handleDeleteAssignment(assignment),
        },
      );
    }
    openContextMenu({ x: e.clientX, y: e.clientY, items });
  }

  async function handleDeleteAssignment(assignment: PlanungAssignment) {
    const ok = await confirm({
      title: "Einsatz löschen",
      message: "Möchten Sie diesen Einsatz wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      confirmLabel: "Löschen",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await deleteAssignment(assignment.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Einsatz konnte nicht gelöscht werden.");
    }
  }

  function handlePlanningContextMenu(e: React.MouseEvent) {
    e.preventDefault();
  }

  function fmtHistoricalAt(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  const planningToolbar = (
    <PlanningToolbar
      isHistorical={isHistorical}
      onToday={resetToTodayView}
      settingsBadge={monteurProposalsCount + feinplanungRequestsCount}
      onOpenSettings={() => setSettingsOpen(true)}
      fullscreen={planningFullscreen}
      onToggleFullscreen={rows.length > 0 ? togglePlanningFullscreen : undefined}
    />
  );

  const planningShell =
    rows.length > 0 ? (
      <div
        ref={planningShellRef}
        className={clsx(
          "mp-planning-shell",
          canViewAblage && "mp-planning-shell--with-ablage",
          planningFullscreen && "mp-planning-shell--overlay",
        )}
      >
        {linkPickBoardActive && (
          <div className="mp-link-pick-banner" role="status">
            <p className="mp-link-pick-banner__text">
              <span className="font-medium">Verknüpfen:</span> Klicken Sie auf Einsatz-Balken in der
              Planung.
              {linkPickState && linkPickState.selectedIds.length > 0
                ? ` (${linkPickState.selectedIds.length} ausgewählt)`
                : ""}
            </p>
            <div className="mp-link-pick-banner__actions">
              {linkPickState && linkPickState.selectedIds.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearLinkPickSelection}>
                  Auswahl leeren
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={endBoardLinkPick}>
                Auswahl beenden
              </Button>
            </div>
          </div>
        )}
        <div className="mp-planning-body">
          <div className="mp-planning-main">
        <div ref={legendRef} className="mp-legend-sticky mp-planning-chrome">
          {planningToolbar}
          <div className="mp-legend-panel">
            <PlanningLegend {...legendProps} />
          </div>
        </div>
        <div className={clsx("mp-line mp-calendar-head", isCompact && "mp-compact")}>
          <PlanningHeadRow {...headRowProps} />
        </div>
        <div
          ref={scrollRef}
          className={clsx(
            "mp-scroll",
            isCompact && "mp-compact",
            extending && "mp-scroll--extending",
          )}
          onPointerDown={onPanPointerDown}
          onContextMenu={handlePlanningContextMenu}
        >
          <div className="mp-grid">
            {rows.map((row) => {
              if (row.kind === "separator") {
                const separatorLabel = row.label?.trim();
                return (
                  <div key={row.id} className="mp-line mp-row-separator">
                    <div
                      className="mp-label mp-separator-label"
                      style={{ width: LABEL_W, minHeight: MP_SEPARATOR_ROW_H }}
                      title={separatorLabel || undefined}
                    >
                      {separatorLabel && (
                        <span className="mp-separator-title">{separatorLabel}</span>
                      )}
                    </div>
                    <div
                      className="mp-track mp-separator-track"
                      style={{ width: totalWidth, minHeight: MP_SEPARATOR_ROW_H }}
                      aria-hidden
                    >
                      <div className="mp-separator-line" />
                    </div>
                  </div>
                );
              }

              const trackLayout = rowLayouts.get(row.id)!;
              const trackHeight = trackLayout.trackHeight;
              const overtimeBalance =
                row.kind === "employee"
                  ? getOvertimeBalanceForEmployee(overtimeByLogin, row.employee_login)
                  : null;
              const isNachunternehmer =
                canViewWorkHours &&
                row.kind === "employee" &&
                !!row.employee_login &&
                nachunternehmerLogins.has(row.employee_login.trim().toLowerCase());
              const labelTitle =
                overtimeBalance != null
                  ? `${rowLabelFull(row)} · Überstunden: ${formatMontageplanOvertimeBalance(overtimeBalance)}`
                  : rowLabelFull(row);
              return (
                <div
                  key={row.id}
                  className={clsx("mp-line", row.kind === "resource" && "mp-row-resource")}
                  data-mp-row-id={row.id}
                >
                  <div
                    className="mp-label"
                    style={{ width: LABEL_W }}
                    title={labelTitle}
                  >
                    {row.color && (
                      <span
                        className={clsx(
                          "inline-block shrink-0 rounded-full",
                          isCompact ? "h-2 w-2" : "h-3 w-3",
                        )}
                        style={{ backgroundColor: row.color }}
                      />
                    )}
                    {overtimeBalance != null ? (
                      <span
                        className={clsx(
                          "mp-label-overtime",
                          overtimeBalance < 0 && "mp-label-overtime-negative",
                        )}
                      >
                        {formatMontageplanOvertimeBalance(overtimeBalance)}
                      </span>
                    ) : null}
                    {isNachunternehmer ? (
                      <span
                        className="mp-label-nu-badge"
                        title="Nachunternehmer"
                      >
                        NU
                      </span>
                    ) : null}
                    <span
                      className={clsx(
                        "mp-label-text",
                        isCompact && "mp-label-abbrev",
                      )}
                    >
                      {isCompact ? rowLabelAbbrev(row) : rowLabelFull(row)}
                    </span>
                  </div>
                  <div
                    ref={(el) => {
                      if (el) trackRefs.current.set(row.id, el);
                      else trackRefs.current.delete(row.id);
                    }}
                    className={clsx(
                      "mp-track",
                      rowIsEditableForViewer(row) && "mp-editable",
                    )}
                    title={
                      monteurCanProposeOnRow(row)
                        ? row.kind === "resource"
                          ? `Klicken, um ${rowLabelFull(row)} zu beantragen`
                          : row.id === board?.viewer_row_id
                            ? "Klicken, um einen Termin zu beantragen"
                            : `Klicken, um einen Termin für ${rowLabelFull(row)} zu beantragen`
                        : undefined
                    }
                    style={{ width: totalWidth, height: trackHeight }}
                    onClick={(e) => handleTrackClick(row, e)}
                    onContextMenu={(e) => handleTrackContextMenu(row, e)}
                  >
                    {todayIdx >= 0 && (
                      <div
                        className="mp-today-marker"
                        style={{ left: todayIdx * DAY_W, width: DAY_W }}
                        aria-hidden
                      />
                    )}

                    <div className="mp-track-bg">
                      {visibleDayCellRange.start > 0 && (
                        <div
                          aria-hidden
                          style={{
                            width: visibleDayCellRange.start * DAY_W,
                            flex: "0 0 auto",
                          }}
                        />
                      )}
                      {days
                        .slice(visibleDayCellRange.start, visibleDayCellRange.end)
                        .map((d) => {
                        const iso = toIso(d);
                        const calendarDay = calendarByDay.get(iso);
                        const officeUnavail =
                          row.kind === "employee" &&
                          isUnavailableDay(row.id, iso, unavailabilities);
                        const unavailBlock = officeUnavail
                          ? findUnavailabilityOnDay(row.id, iso, unavailabilities)
                          : undefined;
                        const nonWorkWeekend =
                          row.kind === "employee"
                            ? isNonWorkDay(d, row.non_work_weekdays)
                            : isWeekend(d);
                        const isToday = isSameDay(d, today);
                        const dayHours =
                          row.kind === "employee"
                            ? getDailyHoursForCell(
                                dailyHoursByLoginDate,
                                row.employee_login,
                                iso,
                              )
                            : null;
                        const dayPaidHours =
                          row.kind === "employee"
                            ? getDailyPaidHoursForCell(
                                dailyPaidHoursByLoginDate,
                                row.employee_login,
                                iso,
                              )
                            : 0;
                        const hoursFullyPaid =
                          dayHours != null && dayPaidHours > 0 && dayPaidHours + 0.001 >= dayHours;
                        return (
                          <div
                            key={iso}
                            title={
                              dayHours != null
                                ? hoursFullyPaid
                                  ? `Arbeitszeit App+Hero: ${formatMontageplanDayHours(dayHours)} (bezahlt)`
                                  : `Arbeitszeit App+Hero: ${formatMontageplanDayHours(dayHours)}`
                                : calendarDay?.label ??
                                  (unavailBlock
                                    ? unavailabilityDayTitle(unavailBlock)
                                    : nonWorkWeekend || officeUnavail
                                      ? nonWorkDayTitle(d, row.non_work_weekdays)
                                      : undefined)
                            }
                            className={clsx(
                              "mp-daycell",
                              officeUnavail && "mp-unavailable",
                              nonWorkWeekend && !officeUnavail && "mp-weekend",
                              !officeUnavail && calendarDay?.kind === "holiday" && "mp-holiday",
                              !officeUnavail && calendarDay?.kind === "bridge" && "mp-bridge",
                              !officeUnavail &&
                                calendarDay?.kind === "betriebsferien" &&
                                "mp-betriebsferien",
                              !officeUnavail && isToday && "mp-today",
                            )}
                            style={{ width: DAY_W }}
                          >
                            {dayHours != null ? (
                              <span
                                className={clsx(
                                  "mp-day-hours",
                                  hoursFullyPaid && "mp-day-hours-paid",
                                )}
                              >
                                {formatMontageplanDayHours(dayHours)}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                      {visibleDayCellRange.end < days.length && (
                        <div
                          aria-hidden
                          style={{
                            width: (days.length - visibleDayCellRange.end) * DAY_W,
                            flex: "0 0 auto",
                          }}
                        />
                      )}
                    </div>

                    {row.kind === "employee" ? (
                      <UnavailabilityReasonLabels
                        rowId={row.id}
                        blocks={unavailabilities}
                        dayIsos={days.map((d) => toIso(d))}
                        dayWidth={DAY_W}
                      />
                    ) : null}

                    {row.system_key === "meros" ? (
                      <MerosStateBars
                        days={days}
                        dayWidth={DAY_W}
                        barHeight={BAR_H}
                        barTop={metrics.rowPadTop}
                        assignments={assignmentsByRow.get(row.id) ?? []}
                      />
                    ) : null}

                    {[...trackLayout.absences.values()].map(({ absence, geom, lane }) => (
                      <AbsenceBar
                        key={`abs-${absenceLayoutKey(absence)}`}
                        absence={absence}
                        geom={geom}
                        dayWidth={DAY_W}
                        barHeight={BAR_H}
                        barTop={barTopForLane(lane, metrics)}
                        canApprove={canApproveAbsences}
                        onApproveClick={setAbsenceApproval}
                      />
                    ))}

                    {showPlannedAssignments
                      ? (assignmentsByRow.get(row.id) ?? []).map((a) => {
                      const laneLayout = trackLayout.assignments.get(a.id);
                      if (!laneLayout) return null;
                      const { geom, lane } = laneLayout;
                      return (
                        <AssignmentBar
                          key={a.id}
                          assignment={a}
                          row={row}
                          geom={geom}
                          canEdit={canDragAssignment(a)}
                          dayWidth={DAY_W}
                          barHeight={BAR_H}
                          barTop={barTopForLane(lane, metrics)}
                          isDragging={dragPreview?.id === a.id}
                          isLinkDragging={
                            linkedDragIdSet.has(a.id) && dragPreview?.id !== a.id
                          }
                          linkHighlight={linkHighlightIds.has(a.id)}
                          planFocusHighlight={planFocusHighlightIds.has(a.id)}
                          onLinkedHoverStart={() => {
                            clearPlanFocusHighlight();
                            highlightLinkedGroup(a.id);
                          }}
                          onLinkedHoverEnd={clearLinkHighlight}
                          linkPickState={
                            linkPickState
                              ? {
                                  primaryId: linkPickState.primaryId,
                                  selectedIds: linkPickState.selectedIds,
                                  excludedIds: linkPickState.excludedIds,
                                  active: linkPickBoardActive,
                                }
                              : undefined
                          }
                          onLinkPickToggle={() => toggleLinkPickAssignment(a.id)}
                          preferViewOnTap={
                            row.system_key !== "meros" && (isCompact || !canEditAssignment(a))
                          }
                          onEdit={() => {
                            if (row.system_key === "meros") return;
                            openEdit(row, a);
                          }}
                          onView={() => {
                            if (row.system_key === "meros") return;
                            openView(row, a);
                          }}
                          onDragStart={(mode, e) => startAssignmentDrag(a, row, mode, e)}
                          onContextMenu={(e) => handleAssignmentContextMenu(row, a, e)}
                        />
                      );
                    })
                      : null}
                    {showBookedAssignments && row.kind === "employee" && row.employee_login
                      ? (bookedByLogin.get(row.employee_login.trim().toLowerCase()) ?? []).map(
                          (span) => {
                            const laneLayout = trackLayout.booked.get(span.key);
                            if (!laneLayout) return null;
                            return (
                              <BookedAssignmentBar
                                key={span.key}
                                span={span}
                                geom={laneLayout.geom}
                                dayWidth={DAY_W}
                                barHeight={BAR_H}
                                barTop={barTopForLane(laneLayout.lane, metrics)}
                                colorMode={bookedColorMode}
                              />
                            );
                          },
                        )
                      : null}
                    {ablageGhost?.rowIds.has(row.id) && (
                      <div
                        className={clsx(
                          "mp-ablage-ghost",
                          !ablageGhost.complete && "is-partial",
                        )}
                        style={{
                          left: ablageGhost.startIdx * DAY_W + (ablageGhost.span === 1 ? 1 : 2),
                          width: Math.max(
                            ablageGhost.span * DAY_W - (ablageGhost.span === 1 ? 2 : 4),
                            8,
                          ),
                          top: metrics.rowPadTop,
                          height: BAR_H,
                          backgroundColor: ablageGhost.color,
                        }}
                      >
                        <span className="mp-ablage-ghost-label">{ablageGhost.title}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
          </div>
          {canViewAblage && (
          <AblagePanel
            canEdit={canEdit}
            refreshToken={ablageRefresh}
            draggingId={ablageDrag?.item.id ?? null}
            hint={
              ablageDrag?.hint ??
              ablageStatus ??
              (ablageParkHover ? "Loslassen, um den Einsatz in die Ablage zu legen" : null)
            }
            busy={schedulingAblage}
            dropRef={ablageDropRef}
            parkHover={ablageParkHover}
            onDragStart={(item) => {
              setAblageStatus(null);
              setAblageDrag({
                item,
                placement: null,
                hint: ablageDragHint(item.person_count, item.day_count, null),
              });
            }}
            onDragMove={updateAblageDrag}
            onDragEnd={(item, clientX, clientY) => {
              void commitAblageDrag(item, clientX, clientY);
            }}
            onDragCancel={() => setAblageDrag(null)}
            onConfirm={confirm}
          />
          )}
        </div>
      </div>
    ) : null;

  return (
    <AppShell
      className={clsx("mp-app-shell", planningFullscreen && "mp-app-shell--immersive")}
      title="Montageplanung"
      subtitle="Einsatzplanung der Mitarbeiter und Ressourcen inklusive Abwesenheiten."
      actions={rows.length === 0 ? planningToolbar : undefined}
    >
      {!planningFullscreen && isHistorical && board && (
        <Alert variant="info">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <p className="font-medium text-label">
                Historischer Stand vom {fmtHistoricalAt(board.historical_at)}
              </p>
              {board.historical_summary && (
                <p className="text-sm text-label-secondary">{board.historical_summary}</p>
              )}
              <p className="text-xs text-label-tertiary">
                Geändert von{" "}
                {board.historical_by_display?.trim() || board.historical_by_login || "Unbekannt"}
              </p>
              <p className="text-xs text-label-tertiary">
                Abwesenheiten und Feiertage entsprechen dem heutigen Stand, nicht dem historischen
                Zeitpunkt.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setHistoryLogId(null)}>
              Zurück zur aktuellen Planung
            </Button>
          </div>
        </Alert>
      )}

      {!planningFullscreen && error && <Alert variant="error">{error}</Alert>}

      {!planningFullscreen &&
        canApproveFeinplanungRequests &&
        feinplanungRequestsCount > 0 && (
          <Alert variant="warning">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-label">
                {feinplanungRequestsCount === 1
                  ? "1 offener Feinplanungs-Antrag wartet auf Freigabe."
                  : `${feinplanungRequestsCount} offene Feinplanungs-Anträge warten auf Freigabe.`}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFeinplanungRequestsOpen(true)}
              >
                Anträge ansehen
              </Button>
            </div>
          </Alert>
        )}

      {!planningFullscreen &&
        canApproveMonteurProposals &&
        monteurProposalsCount > 0 && (
          <Alert variant="warning">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-label">
                {monteurProposalsCount === 1
                  ? "1 offener Terminantrag von einem Monteur wartet auf Prüfung."
                  : `${monteurProposalsCount} offene Terminanträge von Monteuren warten auf Prüfung.`}
              </p>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setMonteurProposalsOpen(true)}
              >
                Anträge ansehen
              </Button>
            </div>
          </Alert>
        )}

      {!planningFullscreen && loading && !board ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : !planningFullscreen && rows.length === 0 ? (
        <Alert variant="info">
          Noch keine Zeilen vorhanden.{" "}
          {canEdit
            ? "Mitarbeiter werden automatisch aus Hero geladen. Ressourcen legst du über „Zeilen verwalten“ an."
            : canCreateMonteurProposal
              ? "Klicke auf eine Mitarbeiter- oder Ressourcenzeile, um einen Termin zu beantragen – auch für Kollegen oder Ressourcen, ohne selbst dabei zu sein."
              : "Bitte wende dich an das Büro, um die Planung einzurichten."}
        </Alert>
      ) : null}

      {planningShell &&
        (planningFullscreen
          ? createPortal(planningShell, document.body)
          : planningShell)}

      {canEdit && (
        <AssignmentDialog
          open={dialog.open}
          onClose={() => setDialog((d) => ({ ...d, open: false }))}
          row={dialog.row}
          assignment={dialog.assignment}
          defaultDate={dialog.defaultDate}
          rowAbsences={dialog.row ? rowAbsences(dialog.row) : []}
          onSaved={load}
          onHistory={
            dialog.assignment ? () => openAssignmentHistory(dialog.assignment!) : undefined
          }
          onConfirm={confirm}
          {...feinplanungApprovalDialogProps(dialog.assignment)}
          {...monteurProposalApprovalDialogProps(dialog.assignment)}
        />
      )}

      {canCreateMonteurProposal && (
        <MonteurProposalDialog
          open={monteurProposalDialog.open}
          onClose={() => setMonteurProposalDialog((d) => ({ ...d, open: false }))}
          row={monteurProposalDialog.row}
          assignment={monteurProposalDialog.assignment}
          defaultDate={monteurProposalDialog.defaultDate}
          rowAbsences={
            monteurProposalDialog.row ? rowAbsences(monteurProposalDialog.row) : []
          }
          employeeRows={(board?.rows ?? []).filter((r) => r.kind === "employee")}
          resourceRows={(board?.rows ?? []).filter((r) => r.kind === "resource")}
          boardAssignments={board?.assignments ?? []}
          viewerLogin={board?.viewer_login}
          viewerRowId={board?.viewer_row_id}
          onSaved={() => {
            load();
            if (canApproveMonteurProposals) {
              refreshPendingMonteurProposals();
            }
            notifyMontageplanungBadgeRefresh();
          }}
          onConfirm={confirm}
        />
      )}

      <AssignmentViewDialog
        open={viewDialog.open}
        onClose={() => setViewDialog((d) => ({ ...d, open: false }))}
        assignment={viewDialog.assignment}
        rowLabel={viewDialog.row?.label || viewDialog.row?.employee_login || null}
        canEdit={
          viewDialog.assignment ? canEditAssignment(viewDialog.assignment) : false
        }
        onHistory={
          viewDialog.assignment
            ? () => openAssignmentHistory(viewDialog.assignment!)
            : undefined
        }
        onEdit={
          viewDialog.assignment && viewDialog.row
            ? () => {
                const { assignment: a, row } = viewDialog;
                if (!a || !row) return;
                setViewDialog({ open: false, assignment: null, row: null });
                openEdit(row, a);
              }
            : undefined
        }
        canEditFeinplanung={
          viewDialog.assignment ? canEditFeinplanung(viewDialog.assignment) : false
        }
        onFeinplanung={
          viewDialog.assignment
            ? () => {
                setViewDialog((d) => ({ ...d, open: false }));
                openFeinplanung(viewDialog.assignment!);
              }
            : undefined
        }
        canRequestFeinplanung={
          viewDialog.assignment ? canRequestFeinplanung(viewDialog.assignment) : false
        }
        feinplanungRequestPending={
          viewDialog.assignment ? feinplanungRequestPending(viewDialog.assignment) : false
        }
        onRequestFeinplanung={
          viewDialog.assignment
            ? () => void handleRequestFeinplanung(viewDialog.assignment!)
            : undefined
        }
        requestingFeinplanung={
          viewDialog.assignment != null && requestingFeinplanungId === viewDialog.assignment.id
        }
        {...feinplanungApprovalDialogProps(viewDialog.assignment)}
      />

      {finePlanning.assignment && (
        <FinePlanningDialog
          open={finePlanning.open}
          onClose={() => setFinePlanning({ open: false, assignment: null })}
          assignmentId={finePlanning.assignment.id}
          assignmentTitle={finePlanning.assignment.title}
          onSaved={load}
        />
      )}

      <AssignmentLinksDialog
        open={assignmentLinks.open}
        onClose={() => {
          clearLinkPickState();
          setAssignmentLinks({ open: false, assignment: null });
        }}
        assignment={assignmentLinks.assignment}
        board={board}
        onChanged={load}
        pickReviewMode={linkPickReviewMode}
        pickSelectedIds={linkPickState?.selectedIds ?? []}
        onStartPickMode={startLinkPickMode}
        onClearLinkPickState={clearLinkPickState}
        onClearPickSelection={clearLinkPickSelection}
      />

      <RowManagerDialog
        open={rowManagerOpen}
        onClose={() => setRowManagerOpen(false)}
        rows={rows}
        onChanged={load}
      />

      <PlanningSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onHistory={() => {
          setHistoryScope(null);
          setHistoryOpen(true);
        }}
        canEdit={canEdit}
        onManageRows={() => setRowManagerOpen(true)}
        canApproveMonteurProposals={canApproveMonteurProposals}
        monteurProposalsCount={monteurProposalsCount}
        onMonteurProposals={() => setMonteurProposalsOpen(true)}
        canApproveFeinplanungRequests={canApproveFeinplanungRequests}
        feinplanungRequestsCount={feinplanungRequestsCount}
        onFeinplanungRequests={() => setFeinplanungRequestsOpen(true)}
      />

      <HistoryDialog
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryScope(null);
        }}
        activeLogId={historyLogId}
        onViewSnapshot={(logId) => setHistoryLogId(logId)}
        scope={historyScope}
        onShowAll={() => setHistoryScope(null)}
      />

      <AbsenceApprovalDialog
        open={absenceApproval != null}
        absence={absenceApproval}
        onClose={() => setAbsenceApproval(null)}
        onReviewed={load}
      />

      {canEdit && (
        <UnavailabilityDialog
          open={unavailDialog.open}
          onClose={() => setUnavailDialog((s) => ({ ...s, open: false }))}
          row={unavailDialog.row}
          existing={unavailDialog.existing}
          defaultFrom={unavailDialog.defaultFrom}
          defaultTo={unavailDialog.defaultTo}
          onSaved={load}
        />
      )}

      {canEdit && (
        <CompanyEventDialog
          open={companyEventDialog.open}
          onClose={() =>
            setCompanyEventDialog((s) => ({ ...s, open: false, assignment: null }))
          }
          employeeRows={employeeRows}
          boardAssignments={board?.assignments ?? []}
          defaultDate={companyEventDialog.defaultDate}
          preselectedRowId={companyEventDialog.preselectedRowId}
          assignment={companyEventDialog.assignment}
          onSaved={load}
          onHistory={
            companyEventDialog.assignment
              ? () => openAssignmentHistory(companyEventDialog.assignment!)
              : undefined
          }
          onConfirm={confirm}
        />
      )}

      {canApproveFeinplanungRequests && (
        <FinePlanningRequestsDialog
          open={feinplanungRequestsOpen}
          onClose={() => setFeinplanungRequestsOpen(false)}
          onReviewed={() => {
            load();
            refreshPendingFeinplanungRequests();
            notifyMontageplanungBadgeRefresh();
          }}
          onHighlightInPlan={highlightRequestInPlan}
        />
      )}

      {canApproveMonteurProposals && (
        <MonteurProposalsDialog
          open={monteurProposalsOpen}
          onClose={() => setMonteurProposalsOpen(false)}
          onReviewed={() => {
            load();
            refreshPendingMonteurProposals();
            notifyMontageplanungBadgeRefresh();
          }}
          onHighlightInPlan={highlightMonteurProposalInPlan}
        />
      )}

      <ContextMenu state={contextMenu} onClose={() => setContextMenu(null)} />
      {confirmDialog}
    </AppShell>
  );
}
